import React, { useEffect, useRef } from "react";
import useEscapeLayer from "../useEscape";

/*
 * THE UNSENT-DRAFT GUARD (frame 1i): Esc, ×, Cancel or the panel closing on an
 * edited composer asks before the words are thrown away. "Throw away your reply
 * to Ella?" with Discard, Keep editing, and a black Save to Gmail when the draft
 * can be filed (can_draft): the yellow was spent on the button that opened the
 * composer.
 *
 * ReplyPanel mounts it, over the page (its own portal), for every way out: its
 * own × and Cancel, its Esc layer, and a close of the panel around it that asked
 * first through composerModel's `unsent` registry. Esc on the guard itself means
 * Keep editing, and so does a click outside it: the safe answer is the one that
 * keeps his words. Keep editing takes the focus when it opens for the same reason,
 * so an Enter pressed on reflex throws nothing away.
 *
 * Props:
 *   name       string, the recipient's first name ("Ella")
 *   what       string, "reply" | "email": "your reply to Ella" / "your email to Dana"
 *   canSave    boolean, offer Save to Gmail (the composer's data.can_draft)
 *   busy       boolean, a save in flight
 *   error      string | null, shown beside the buttons when the save failed
 *   onDiscard  () => void
 *   onKeep     () => void
 *   onSave     () => void
 */
export default function UnsentGuard({ name = "them", what = "reply", canSave = false, busy = false, error = null, onDiscard, onKeep, onSave }) {
  useEscapeLayer(true, () => !busy && onKeep && onKeep());
  const keepRef = useRef(null);
  useEffect(() => {
    if (keepRef.current) keepRef.current.focus();
  }, []);
  return (
    <div className="rec-confirm" role="alertdialog" aria-label={`Throw away your ${what} to ${name}?`}>
      <div className="rec-confirm__title">Throw away your {what} to {name}?</div>
      <div className="rec-body">
        You edited this draft. {canSave ? "Save it to Gmail to finish later, or keep editing." : "Keep editing, or discard it."}
      </div>
      <div className="rec-confirm__actions">
        {error && <span className="rec-err">{error}</span>}
        <button type="button" className="rec-btn rec-btn--plain" disabled={busy} onClick={onDiscard}>Discard</button>
        <button ref={keepRef} type="button" className="rec-btn rec-btn--plain" disabled={busy} onClick={onKeep}>Keep editing</button>
        {canSave && (
          <button type="button" className="rec-btn rec-btn--dark" disabled={busy} onClick={onSave}>
            {busy ? "Saving…" : "Save to Gmail"}
          </button>
        )}
      </div>
    </div>
  );
}
